import { FLIGHT_TYPE_LABEL } from "./generator";
import { haversineNm } from "./geo";
import { TICKET_LIFECYCLE_LABEL, ticketLifecycle } from "./opsBoard";
import type { CompanyProfile, Mission, PilotProfile, TicketLifecycle } from "./types";

/** Local SI Windows client. CORS is open (`*`). Empty `{}` until a flight is up. */
export const SI_FLIGHTJSON_URL = "http://localhost:43117/flightJSON";

/** Documented SAPI host. Prefer `hostname` from flightJSON when present. */
export const SAPI_DEFAULT_HOST = "https://apipri.sayintentions.ai";

/**
 * Public tracker endpoint. Requires a **partner** `x-api-key` (SI Discord), not
 * invented here. The older `lambda.sayintentions.ai/tracker/map` URL now 404s.
 * Live aircraft lat/lon prefers SimConnect on the desk. flightJSON may carry
 * `flight_details.coordinates` as a secondary SI sample; Active Flights is the
 * tracker path when a partner key is present.
 */
export const SI_ACTIVE_FLIGHTS_URL = "https://tracker.sayintentions.ai/api/public/active-flights";

/**
 * `L:SIAI_FLIGHT_PHASE` values (MSFS LVAR). Live lat/lon is native SimConnect
 * on the Windows .exe. This phase code is still a **heuristic** from flightJSON
 * (+ tracker if any) — we do not read the LVAR.
 */
export const SI_FLIGHT_PHASE_LABEL: Record<number, string> = {
  0: "Unknown",
  1: "Origin ground",
  2: "Departure",
  3: "En-route",
  4: "Arrival",
  5: "Destination ground",
};

export type SiClientState = "disabled" | "offline" | "idle" | "live";

/**
 * Companion polls must not flash System status Network text back to offline
 * while the last settled poll was idle or live.
 */
export function siClientWhileProbing(current: SiClientState, probed: SiClientState): SiClientState {
  if (probed === "offline" && (current === "idle" || current === "live")) return current;
  return probed;
}

export type SiFlightSession = {
  apiKey: string;
  flightId: string;
  callsign: string;
  callsignIcao: string;
  originIcao: string;
  destIcao: string;
  currentAirport: string;
  route: string;
  hostname: string;
  originLat: number | null;
  originLon: number | null;
  destLat: number | null;
  destLon: number | null;
  gateLat: number | null;
  gateLon: number | null;
  assignedGate: string;
  clearedForTakeoff: boolean;
  clearedForLanding: boolean;
  distanceToRunwayNm: number | null;
  rawPresent: boolean;
};

export type SiActiveFlight = {
  flightId: string;
  lat: number;
  lon: number;
  heading: number | null;
  altitude: number | null;
  altitudeAgl: number | null;
  airspeed: number | null;
  squawk: number | null;
  trackDeg: number | null;
  callsign: string;
  tailNumber: string;
  flightIcao: string;
  originIcao: string;
  destIcao: string;
};

export type SiPosition = {
  lat: number;
  lon: number;
  heading: number | null;
  altitude: number | null;
  altitudeAgl: number | null;
  airspeed: number | null;
  squawk: number | null;
  trackDeg: number | null;
  source: "active-flights" | "flightjson";
};

export type SiPhaseGuess = {
  code: number;
  label: string;
  source: "heuristic";
  /** Why we guessed this — never claim we read `L:SIAI_FLIGHT_PHASE`. */
  note: string;
};

export type ImportVaPayload = {
  va_api_key: string;
  dispatcher_data: string;
  skyops_data: string;
};

/** Taxi / airborne / diverting — desk ticket truth may lock the live SI session. */
export const SI_LOCK_LIFECYCLES: readonly TicketLifecycle[] = ["taxi", "airborne", "diverting"];

/** Partner Active Flights rows inside this radius (NM) count as nearby traffic. */
export const SI_NEARBY_TRAFFIC_NM = 200;

export type SiNetworkState = "off" | "offline" | "idle" | "live";

export type SiTicketLockDecision = {
  push: boolean;
  reason: "lock" | "missing-keys" | "no-ticket" | "no-session" | "unchanged";
  fingerprint: string;
};

export type SiLiveDeskStatus = {
  network: SiNetworkState;
  connected: boolean;
  phaseLabel: string;
  phaseNote: string;
  lockLabel: string;
  trafficLabel: string;
};

/** Map-agnostic traffic point — Mapbox (or any engine) can plot these. */
export type SiTrafficFeature = {
  id: string;
  lat: number;
  lon: number;
  heading: number | null;
  altitude: number | null;
  callsign: string;
  originIcao: string;
  destIcao: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asBool(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const t = asText(value).toLowerCase();
  return t === "1" || t === "true" || t === "yes";
}

function icao(value: unknown): string {
  return asText(value).toUpperCase();
}

export function isEmptyFlightJson(raw: unknown): boolean {
  if (raw == null) return true;
  if (typeof raw !== "object") return true;
  return Object.keys(raw as object).length === 0;
}

export function parseFlightJson(raw: unknown): SiFlightSession | null {
  if (isEmptyFlightJson(raw)) return null;
  const root = asRecord(raw);
  if (!root) return null;
  const details = asRecord(root.flight_details) ?? root;
  const current = asRecord(details.current_flight) ?? {};
  const flightId = asText(details.flight_id);
  const callsign = asText(details.callsign) || asText(details.callsign_icao);
  const originIcao = icao(current.flight_origin) || icao(details.flight_origin);
  const destIcao = icao(current.flight_destination) || icao(details.flight_destination);
  const hasIdentity = Boolean(flightId || callsign || originIcao || destIcao || asText(details.api_key));
  if (!hasIdentity) return null;

  return {
    apiKey: asText(details.api_key),
    flightId,
    callsign,
    callsignIcao: asText(details.callsign_icao) || callsign,
    originIcao,
    destIcao,
    currentAirport: icao(details.current_airport),
    route: asText(current.flight_plan_route) || asText(details.flight_plan_route),
    hostname: asText(details.hostname) || SAPI_DEFAULT_HOST,
    originLat: asNumber(current.flight_plan_origin_lat),
    originLon: asNumber(current.flight_plan_origin_lon),
    destLat: asNumber(current.flight_destination_lat),
    destLon: asNumber(current.flight_destination_lon),
    gateLat: asNumber(current.assigned_gate_lat),
    gateLon: asNumber(current.assigned_gate_lon),
    assignedGate: asText(current.assigned_gate),
    clearedForTakeoff: asBool(details.cleared_for_takeoff) || asBool(current.cleared_for_takeoff),
    clearedForLanding: asBool(details.cleared_for_landing) || asBool(current.cleared_for_landing),
    distanceToRunwayNm: asNumber(details.distance_to_runway),
    rawPresent: true,
  };
}

export function resolveSiApiKey(session: SiFlightSession | null, override: string): string {
  const fromFlight = session?.apiKey?.trim() ?? "";
  if (fromFlight) return fromFlight;
  return override.trim();
}

export function sapiBaseUrl(session: SiFlightSession | null): string {
  const host = (session?.hostname || SAPI_DEFAULT_HOST).replace(/\/$/, "");
  return `${host}/sapi`;
}

export function parseActiveFlights(raw: unknown): SiActiveFlight[] {
  const list = Array.isArray(raw)
    ? raw
    : asRecord(raw)?.flights && Array.isArray(asRecord(raw)?.flights)
      ? (asRecord(raw)?.flights as unknown[])
      : [];
  const out: SiActiveFlight[] = [];
  for (const row of list) {
    const rec = asRecord(row);
    if (!rec) continue;
    const lat = asNumber(rec.lat);
    const lon = asNumber(rec.lon);
    if (lat == null || lon == null) continue;
    out.push({
      flightId: asText(rec.flight_id),
      lat,
      lon,
      heading: asNumber(rec.heading),
      altitude: asNumber(rec.altitude),
      altitudeAgl: asNumber(rec.altitude_agl),
      airspeed: asNumber(rec.airspeed),
      squawk: asNumber(rec.squawk ?? rec.transponder ?? rec.xpdr),
      trackDeg: asNumber(rec.track ?? rec.ground_track ?? rec.trk),
      callsign: asText(rec.callsign),
      tailNumber: asText(rec.tail_number),
      flightIcao: asText(rec.flight_icao),
      originIcao: icao(rec.origin) || icao(rec.flight_plan_origin),
      destIcao: icao(rec.final_destination) || icao(rec.flight_plan_destination),
    });
  }
  return out;
}

function normCall(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function matchActiveFlight(
  flights: SiActiveFlight[],
  session: SiFlightSession | null,
): SiActiveFlight | null {
  if (!flights.length || !session) return null;
  if (session.flightId) {
    const byId = flights.find((f) => f.flightId && f.flightId === session.flightId);
    if (byId) return byId;
  }
  const want = [session.callsignIcao, session.callsign].map(normCall).filter(Boolean);
  if (want.length) {
    const byCall = flights.find((f) => {
      const keys = [f.flightIcao, f.tailNumber, f.callsign].map(normCall).filter(Boolean);
      return keys.some((k) => want.includes(k));
    });
    if (byCall) return byCall;
  }
  if (session.originIcao && session.destIcao) {
    const routeHits = flights.filter(
      (f) => f.originIcao === session.originIcao && f.destIcao === session.destIcao,
    );
    if (routeHits.length === 1) return routeHits[0] ?? null;
  }
  return null;
}

export function positionFromActiveFlight(flight: SiActiveFlight | null): SiPosition | null {
  if (!flight) return null;
  return {
    lat: flight.lat,
    lon: flight.lon,
    heading: flight.heading,
    altitude: flight.altitude,
    altitudeAgl: flight.altitudeAgl,
    airspeed: flight.airspeed,
    squawk: flight.squawk,
    trackDeg: flight.trackDeg,
    source: "active-flights",
  };
}

function validLatLon(lat: number, lon: number): boolean {
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

function coordsRecord(value: unknown): SiPosition | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const lat = asNumber(rec.lat ?? rec.latitude ?? rec.Latitude);
  const lon = asNumber(rec.lon ?? rec.lng ?? rec.longitude ?? rec.Longitude);
  if (lat == null || lon == null || !validLatLon(lat, lon)) return null;
  return {
    lat,
    lon,
    heading: asNumber(rec.heading ?? rec.hdg ?? rec.Heading),
    altitude: asNumber(rec.altitude ?? rec.alt ?? rec.altitudeFt ?? rec.Altitude),
    altitudeAgl: asNumber(rec.altitudeAgl ?? rec.agl ?? rec.AltitudeAGL),
    airspeed: asNumber(rec.airspeed ?? rec.ias ?? rec.Airspeed),
    squawk: asNumber(rec.squawk ?? rec.transponder ?? rec.xpdr),
    trackDeg: asNumber(rec.track ?? rec.trackDeg ?? rec.ground_track ?? rec.trk),
    source: "flightjson",
  };
}

/**
 * Secondary SI lat/lon from `flight_details.coordinates` (or a sibling
 * coordinates object). Does not use airport origin/dest fields.
 */
export function parseFlightJsonCoordinates(raw: unknown): SiPosition | null {
  const root = asRecord(raw);
  if (!root) return null;
  const details = asRecord(root.flight_details) ?? root;
  const current = asRecord(details.current_flight) ?? {};
  const fromObjects =
    coordsRecord(details.coordinates) ??
    coordsRecord(current.coordinates) ??
    coordsRecord(root.coordinates) ??
    coordsRecord(details.position);
  if (fromObjects) return fromObjects;

  if (typeof details.coordinates === "string") {
    const parts = details.coordinates.split(/[,;\s]+/).filter(Boolean);
    if (parts.length >= 2) {
      const lat = asNumber(parts[0]);
      const lon = asNumber(parts[1]);
      if (lat != null && lon != null && validLatLon(lat, lon)) {
        return {
          lat,
          lon,
          heading: null,
          altitude: null,
          altitudeAgl: null,
          airspeed: null,
          squawk: null,
          trackDeg: null,
          source: "flightjson",
        };
      }
    }
  }
  return null;
}

export function inferFlightPhase(
  session: SiFlightSession | null,
  track: SiActiveFlight | null = null,
): SiPhaseGuess {
  if (!session) {
    return { code: 0, label: SI_FLIGHT_PHASE_LABEL[0] ?? "Unknown", source: "heuristic", note: "No flightJSON session." };
  }

  const here = session.currentAirport;
  const origin = session.originIcao;
  const dest = session.destIcao;

  if (track) {
    const agl = track.altitudeAgl;
    const spd = track.airspeed;
    if (agl != null && agl < 80 && spd != null && spd < 40) {
      if (dest && (here === dest || track.destIcao === dest)) {
        return {
          code: 5,
          label: SI_FLIGHT_PHASE_LABEL[5] ?? "Destination ground",
          source: "heuristic",
          note: "Tracker AGL/speed look parked; airport matches destination.",
        };
      }
      if (origin && (here === origin || track.originIcao === origin)) {
        return {
          code: 1,
          label: SI_FLIGHT_PHASE_LABEL[1] ?? "Origin ground",
          source: "heuristic",
          note: "Tracker AGL/speed look parked; airport matches origin.",
        };
      }
    }
    if ((agl != null && agl > 1500) || (track.altitude != null && track.altitude > 4000)) {
      return {
        code: 3,
        label: SI_FLIGHT_PHASE_LABEL[3] ?? "En-route",
        source: "heuristic",
        note: "Tracker altitude looks airborne / cruise.",
      };
    }
  }

  if (dest && here === dest) {
    return {
      code: 5,
      label: SI_FLIGHT_PHASE_LABEL[5] ?? "Destination ground",
      source: "heuristic",
      note: "flightJSON current_airport equals destination.",
    };
  }
  if (session.clearedForLanding) {
    return {
      code: 4,
      label: SI_FLIGHT_PHASE_LABEL[4] ?? "Arrival",
      source: "heuristic",
      note: "flightJSON cleared_for_landing is set. Not L:SIAI_FLIGHT_PHASE.",
    };
  }
  if (session.clearedForTakeoff && origin && (here === origin || !here)) {
    return {
      code: 2,
      label: SI_FLIGHT_PHASE_LABEL[2] ?? "Departure",
      source: "heuristic",
      note: "flightJSON cleared_for_takeoff is set. Not L:SIAI_FLIGHT_PHASE.",
    };
  }
  if (origin && here === origin) {
    return {
      code: 1,
      label: SI_FLIGHT_PHASE_LABEL[1] ?? "Origin ground",
      source: "heuristic",
      note: "flightJSON current_airport equals origin.",
    };
  }
  if (origin && dest && here && here !== origin && here !== dest) {
    return {
      code: 3,
      label: SI_FLIGHT_PHASE_LABEL[3] ?? "En-route",
      source: "heuristic",
      note: "current_airport is neither origin nor destination.",
    };
  }
  if (origin && dest && !here) {
    return {
      code: 3,
      label: SI_FLIGHT_PHASE_LABEL[3] ?? "En-route",
      source: "heuristic",
      note: "Route is filed and current_airport is empty.",
    };
  }
  return {
    code: 0,
    label: SI_FLIGHT_PHASE_LABEL[0] ?? "Unknown",
    source: "heuristic",
    note: "flightJSON present but phase fields are inconclusive. LVAR not readable.",
  };
}

export function phaseLooksAirborne(phase: SiPhaseGuess): boolean {
  return phase.code === 2 || phase.code === 3 || phase.code === 4;
}

export function canPushVaData(apiKey: string, vaApiKey: string): boolean {
  return Boolean(apiKey.trim() && vaApiKey.trim());
}

export function vaPushDisabledReason(apiKey: string, vaApiKey: string): string {
  const missing: string[] = [];
  if (!apiKey.trim()) missing.push("a tracker API key (flightJSON or Settings override)");
  if (!vaApiKey.trim()) missing.push("a VA ops key (from companion support)");
  if (!missing.length) return "";
  return `Send ops brief needs ${missing.join(" and ")}. Do not invent credentials.`;
}

function paxLine(mission: Mission): string {
  const count = typeof mission.paxCount === "number" && Number.isFinite(mission.paxCount) ? mission.paxCount : 0;
  const names = Array.isArray(mission.paxNames) ? mission.paxNames.filter(Boolean).join(", ") : "";
  return names ? `${count} pax (${names})` : `${count} pax`;
}

function cargoAmountLb(mission: Mission): number {
  return typeof mission.cargoLb === "number" && Number.isFinite(mission.cargoLb) ? mission.cargoLb : 0;
}

function cargoLine(mission: Mission): string {
  const cargo = cargoAmountLb(mission);
  return cargo ? `${cargo} lb cargo` : "no special cargo filed";
}

function boardStatusLine(mission: Mission): string {
  const life = ticketLifecycle(mission);
  const label = TICKET_LIFECYCLE_LABEL[life] ?? life;
  const ferry = mission.flightType === "ferry" ? " Ferry: yes." : "";
  return `Desk ticket status: ${label}.${ferry}`;
}

function divertLines(mission: Mission): string[] {
  const dest = (mission.destIcao ?? "").trim().toUpperCase();
  const divert = (mission.divertToIcao ?? "").trim().toUpperCase();
  if (!divert) return [];
  const reason = (mission.divertReason ?? "").trim();
  const life = ticketLifecycle(mission);
  const phase = life === "diverted" ? "Diverted" : life === "diverting" ? "Diverting" : "Divert filed";
  return [`Original dest: ${dest || "—"}.`, `${phase} to ${divert}${reason ? ` — ${reason}` : ""}.`];
}

export function buildDispatcherData(
  mission: Mission,
  company: CompanyProfile,
  pilot: Pick<PilotProfile, "name">,
): string {
  const typeLabel = FLIGHT_TYPE_LABEL[mission.flightType] ?? mission.flightType;
  return [
    `Virtual Airline / operator: ${company.operatorName} (home ${company.homeIcao}).`,
    `Software desk: Northstar Dispatch. This is an in-app Celtic Aviation Services charter — family-friendly aviation only.`,
    `Flight / callsign: ${mission.callsign}. Registration: ${mission.registration}. Aircraft: ${mission.aircraftName}.`,
    `Captain / company pilot: ${pilot.name}.`,
    `Route: ${mission.originIcao} (${mission.originName}, ${mission.originCity}) → ${mission.destIcao} (${mission.destName}, ${mission.destCity}).`,
    ...divertLines(mission),
    `Distance ${mission.distanceNm} nm. Planned ETE ${mission.eteMin} min. Track ${mission.trackTrue}°T.`,
    `STD (ISO): ${mission.departureIso}.`,
    boardStatusLine(mission),
    `Load: ${paxLine(mission)}. ${cargoLine(mission)}.`,
    `Charter type: ${typeLabel}. Purpose: ${mission.purpose}`,
    `Scenario: ${mission.scenario}`,
    `Fuel plan (desk estimate): ${mission.fuelGal} gal / ${mission.fuelLbs} lb.`,
    `Company flavour: Celtic Aviation Services is a Cape Breton charter shop. Dispatch copy stays aviation-themed and family-friendly. Sign off with a quiet “clear skies.”`,
  ].join("\n");
}

export function buildSkyopsData(
  mission: Mission,
  company: CompanyProfile,
): string {
  const typeLabel = FLIGHT_TYPE_LABEL[mission.flightType] ?? mission.flightType;
  const flavour =
    mission.flightType === "medical"
      ? "Medevac charter. Treat as a time-sensitive patient transfer. No drama, no fiction beyond a standard air-ambulance brief. Family-friendly."
      : mission.flightType === "ferry"
        ? "Ferry / positioning. Light cabin, maintenance or pad move. Keep SkyOps prompts operational, not cinematic."
        : `Passenger charter (${typeLabel}). Celtic Aviation Services mission — golf, sun, corporate, or VIP as filed. No combat, no rescue theatrics unless the ticket is medical.`;

  return [
    `SkyOps / VA-Link hook for ${company.operatorName}.`,
    `Mission flavour: Celtic charter. ${flavour}`,
    `Callsign ${mission.callsign} / ${mission.registration}. ${mission.originIcao}–${mission.destIcao}.`,
    ...divertLines(mission),
    `STD ${mission.departureIso}. ${boardStatusLine(mission)}`,
    `${paxLine(mission)}. Cargo ${cargoAmountLb(mission)} lb.`,
    `Tasking: file and fly the charter as dispatched. Check in with Celtic dispatch on the desk, not invented phone numbers.`,
    `getCommsHistory mission blob is not pulled this slice — this skyops_data is the charter brief SI should use.`,
    `Communications stay family-friendly and aviation-themed.`,
  ].join("\n");
}

export function buildImportVaPayload(
  mission: Mission,
  vaApiKey: string,
  company: CompanyProfile,
  pilot: Pick<PilotProfile, "name">,
): ImportVaPayload {
  return {
    va_api_key: vaApiKey.trim(),
    dispatcher_data: buildDispatcherData(mission, company, pilot),
    skyops_data: buildSkyopsData(mission, company),
  };
}

export function sessionMatchesMission(
  session: SiFlightSession,
  mission: Pick<Mission, "originIcao" | "destIcao" | "callsign" | "registration"> & {
    divertToIcao?: string;
  },
): boolean {
  if (session.originIcao && session.destIcao) {
    if (session.originIcao === mission.originIcao && session.destIcao === mission.destIcao) return true;
    const divert = (mission.divertToIcao ?? "").trim().toUpperCase();
    if (divert && session.originIcao === mission.originIcao && session.destIcao === divert) return true;
  }
  const want = [mission.callsign, mission.registration].map(normCall).filter(Boolean);
  const have = [session.callsign, session.callsignIcao].map(normCall).filter(Boolean);
  return want.some((w) => have.includes(w));
}

export function ticketIsSiLockable(
  mission: Pick<Mission, "status" | "lifecycle"> | null | undefined,
): boolean {
  if (!mission) return false;
  if (mission.status === "cancelled" || mission.status === "completed") return false;
  const life = ticketLifecycle(mission);
  if ((SI_LOCK_LIFECYCLES as readonly string[]).includes(life)) return true;
  return mission.status === "in-progress";
}

export function siTicketLockFingerprint(mission: Mission): string {
  return [
    mission.id,
    mission.callsign,
    mission.originIcao,
    mission.destIcao,
    mission.divertToIcao ?? "",
    mission.divertReason ?? "",
    ticketLifecycle(mission),
    mission.status,
    mission.flightType,
    mission.departureIso,
    String(typeof mission.paxCount === "number" ? mission.paxCount : 0),
    String(cargoAmountLb(mission)),
  ].join("|");
}

export function shouldPushSiTicketLock(input: {
  ticket: Mission | null | undefined;
  session: SiFlightSession | null;
  apiKey: string;
  vaApiKey: string;
  lastFingerprint?: string;
}): SiTicketLockDecision {
  if (!canPushVaData(input.apiKey, input.vaApiKey)) {
    return { push: false, reason: "missing-keys", fingerprint: "" };
  }
  if (!input.ticket || !ticketIsSiLockable(input.ticket)) {
    return { push: false, reason: "no-ticket", fingerprint: "" };
  }
  if (!input.session) {
    return { push: false, reason: "no-session", fingerprint: "" };
  }
  const fingerprint = siTicketLockFingerprint(input.ticket);
  if (fingerprint && fingerprint === input.lastFingerprint) {
    return { push: false, reason: "unchanged", fingerprint };
  }
  return { push: true, reason: "lock", fingerprint };
}

export function siNetworkState(enabled: boolean, client: SiClientState): SiNetworkState {
  if (!enabled || client === "disabled") return "off";
  if (client === "offline") return "offline";
  if (client === "idle") return "idle";
  return "live";
}

export function buildSiLiveDeskStatus(input: {
  enabled: boolean;
  client: SiClientState;
  session: SiFlightSession | null;
  phase: SiPhaseGuess;
  ticket?: (Pick<Mission, "originIcao" | "destIcao" | "callsign" | "registration" | "status" | "lifecycle"> & {
    divertToIcao?: string;
  }) | null;
  trafficCount?: number;
  trackerError?: string;
}): SiLiveDeskStatus {
  const network = siNetworkState(input.enabled, input.client);
  const connected = network === "live" || network === "idle";
  let lockLabel = "Ticket lock —";
  if (input.session && input.ticket && sessionMatchesMission(input.session, input.ticket)) {
    lockLabel = `Ticket lock ${input.ticket.callsign || "LIVE"}`;
  } else if (input.session && input.ticket && ticketIsSiLockable(input.ticket)) {
    lockLabel = "Ticket lock pending match";
  } else if (input.session) {
    lockLabel = "SI session (no desk ticket)";
  }
  const trafficCount = input.trafficCount ?? 0;
  const err = (input.trackerError ?? "").trim();
  let trafficLabel = "SI traffic —";
  if (trafficCount > 0) {
    trafficLabel = `SI traffic ${trafficCount} nearby`;
  } else if (err && /partner|api key|x-api-key|401|403/i.test(err)) {
    trafficLabel = "SI traffic: no partner key";
  } else if (err) {
    trafficLabel = "SI traffic unavailable";
  } else if (network === "live") {
    trafficLabel = "SI traffic: none nearby";
  }
  return {
    network,
    connected,
    phaseLabel: input.session ? input.phase.label : network === "idle" ? "Idle" : "—",
    phaseNote: input.phase.note,
    lockLabel,
    trafficLabel,
  };
}

export function isOwnSiTraffic(
  flight: SiActiveFlight,
  own: Pick<SiFlightSession, "flightId" | "callsign" | "callsignIcao"> | SiActiveFlight | null,
): boolean {
  if (!own) return false;
  const ownId = "flightId" in own ? own.flightId : "";
  if (ownId && flight.flightId && ownId === flight.flightId) return true;
  const want = [
    "callsignIcao" in own ? own.callsignIcao : "",
    own.callsign,
    "flightIcao" in own ? own.flightIcao : "",
    "tailNumber" in own ? own.tailNumber : "",
  ]
    .map(normCall)
    .filter(Boolean);
  const have = [flight.flightIcao, flight.tailNumber, flight.callsign].map(normCall).filter(Boolean);
  return want.some((w) => have.includes(w));
}

export function nearbySiTraffic(
  flights: SiActiveFlight[],
  own: SiFlightSession | SiActiveFlight | null,
  origin?: { lat: number; lon: number } | null,
  radiusNm = SI_NEARBY_TRAFFIC_NM,
): SiActiveFlight[] {
  if (!origin || !Number.isFinite(origin.lat) || !Number.isFinite(origin.lon)) return [];
  return flights.filter((flight) => {
    if (isOwnSiTraffic(flight, own)) return false;
    return haversineNm(origin.lat, origin.lon, flight.lat, flight.lon) <= radiusNm;
  });
}

export function siTrafficFeatures(flights: SiActiveFlight[]): SiTrafficFeature[] {
  return flights.map((flight, i) => ({
    id: flight.flightId || `${normCall(flight.callsign) || "si"}-${i}`,
    lat: flight.lat,
    lon: flight.lon,
    heading: flight.heading,
    altitude: flight.altitude,
    callsign: flight.callsign || flight.flightIcao || flight.tailNumber,
    originIcao: flight.originIcao,
    destIcao: flight.destIcao,
  }));
}
